import { useState, useEffect, useRef, forwardRef, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../../api/client'
import { API_ENDPOINTS } from '../../api/endpoints'
import { FiChevronDown, FiX } from 'react-icons/fi'
import { useLanguageStore } from '../../store/languageStore'
import { useTranslation } from '../../hooks/useTranslation'
import { soleOptionIdFromList, useAutoSelectSingleOption } from '../../hooks/useAutoSelectSingleOption'
import DropdownSearchHeader from './DropdownSearchHeader'
import { useLookupRefresh } from '../../hooks/useLookupRefresh'
import { useDropdownFieldConfig } from '../../hooks/useDropdownFieldConfig'
import { formatConfiguredLabel } from '../../utils/dropdownFieldConfig'
import { mapPagedResponse, useInfiniteLookup } from '../../hooks/useInfiniteLookup'

interface Store {
  id: string
  name: string
  branchId?: string
  isActive: boolean
}

interface StoreDropdownProps {
  value?: string | number
  onChange: (value: string | undefined) => void
  branchId?: string
  placeholder?: string
  className?: string
  error?: string
  disabled?: boolean
  /** When true (default), selects the only warehouse if exactly one store is available for the current branch filter. */
  autoSelectSingle?: boolean
}

function mapStoreRow(store: any): Store | null {
  const id = String(store.id || store.ID || '').trim()
  const name = String(store.name || store.Name || '').trim()
  if (!id || !name || id === 'undefined' || name === 'undefined') return null
  const branchIdValue = store.branchId || store.Branch_ID || store.BranchId
  const isActive =
    store.isActive !== undefined
      ? Boolean(store.isActive)
      : store.IsActive !== undefined
        ? Boolean(store.IsActive)
        : true
  return { id, name, branchId: branchIdValue, isActive }
}

const StoreDropdown = forwardRef<HTMLDivElement, StoreDropdownProps>(
  (
    {
      value,
      onChange,
      branchId,
      placeholder,
      className = '',
      error,
      disabled = false,
      autoSelectSingle = true,
    }: StoreDropdownProps,
    ref
  ) => {
    const { language } = useLanguageStore()
    const { translate: t } = useTranslation()
    const isRTL = language === 'ar'
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const dropdownRef = useRef<HTMLDivElement>(null)
    const portalRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLDivElement>(null)
    const fieldConfig = useDropdownFieldConfig('store')
    const getId = useCallback((s: Store) => s.id, [])

    const setRefs = (node: HTMLDivElement | null) => {
      dropdownRef.current = node
      if (typeof ref === 'function') {
        ref(node)
      } else if (ref) {
        ;(ref as React.MutableRefObject<HTMLDivElement | null>).current = node
      }
    }

    const normalizedBranchId =
      branchId !== undefined && branchId !== null ? String(branchId).trim() : ''

    const {
      items: stores,
      isLoading,
      isFetching,
      isFetchingNextPage,
      refetch,
      sentinelRef,
    } = useInfiniteLookup<Store>({
      queryKey: ['stores', 'dropdown', normalizedBranchId || 'all'],
      enabled: !disabled && (isOpen || !value || autoSelectSingle),
      pageSize: 40,
      searchTerm,
      searchFields: fieldConfig.searchFields,
      getId,
      scrollRootRef: listRef,
      fetchPage: async ({ page, pageSize, search, searchFields }) => {
        const params: Record<string, string | number> = { page, pageSize }
        if (normalizedBranchId) params.branchId = normalizedBranchId
        if (search) params.searchTerm = search
        if (searchFields.length > 0) params.searchFields = searchFields.join(',')
        const response = await apiClient.get(API_ENDPOINTS.stores.list, { params })
        return mapPagedResponse(response.data, mapStoreRow, page, pageSize)
      },
    })

    const normalizeId = (id: string | number | undefined): string => {
      if (id == null) return ''
      return String(id).trim()
    }

    const valueStr = normalizeId(value)

    const { data: selectedById, error: selectedFetchError } = useQuery({
      queryKey: ['stores', 'selected', valueStr],
      enabled: !!valueStr && !stores.some((s) => normalizeId(s.id) === valueStr),
      queryFn: async () => {
        const response = await apiClient.get(API_ENDPOINTS.stores.get(valueStr))
        return mapStoreRow(response.data)
      },
    })

    const filteredStores = useMemo(() => {
      if (!selectedById?.id) return stores
      if (stores.some((s) => normalizeId(s.id) === normalizeId(selectedById.id))) return stores
      return [selectedById, ...stores]
    }, [stores, selectedById])

    const selectedStore =
      filteredStores.find((store) => normalizeId(store.id) === valueStr) ||
      selectedById ||
      undefined

    const soleStoreIdStr = soleOptionIdFromList(stores, (s) => s.id)

    const applySoleStore = useCallback(() => {
      const id = stores[0]?.id
      if (!id) return
      onChange(normalizeId(id))
    }, [stores, onChange])

    const { markUserCleared } = useAutoSelectSingleOption({
      soleIdStr: soleStoreIdStr,
      currentValue: value,
      onApplySole: applySoleStore,
      isLoading,
      skip: !!selectedFetchError,
      disabled,
      autoSelectSingle,
      resetKey: normalizedBranchId,
    })

    const { onRefresh, isRefreshing } = useLookupRefresh({
      refetch,
      isFetching,
      items: stores,
      selectedId: value,
      onClearSelection: () => onChange(undefined),
      language,
    })

    useEffect(() => {
      if (!isOpen) return

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node
        if (dropdownRef.current?.contains(target) || portalRef.current?.contains(target)) {
          return
        }
        setIsOpen(false)
        setSearchTerm('')
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen])

    const handleSelect = (storeId: string) => {
      onChange(storeId)
      setIsOpen(false)
      setSearchTerm('')
    }

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation()
      markUserCleared()
      onChange(undefined)
      setSearchTerm('')
    }

    return (
      <div className={`relative ${className}`} ref={setRefs}>
        <input type="hidden" value={valueStr} readOnly aria-hidden />

        <div
          onClick={() => {
            if (!disabled) setIsOpen(!isOpen)
          }}
          className={`input-field cursor-pointer flex items-center justify-between ${
            error ? 'border-red-500' : ''
          } ${isRTL ? 'flex-row-reverse' : 'flex-row'} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        >
          <span
            className={`flex-1 ${isRTL ? 'text-right' : 'text-left'} ${
              !value ? 'text-gray-400' : 'text-gray-800'
            }`}
          >
            {selectedStore
              ? formatConfiguredLabel(selectedStore, 'store', fieldConfig.triggerFields, language)
              : placeholder || t('stores.selectStore')}
          </span>
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
            {valueStr && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="text-gray-400 hover:text-red-500 transition-colors"
                title={t('common.clear')}
              >
                <FiX />
              </button>
            )}
            <FiChevronDown
              className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </div>

        {error && (
          <p className={`mt-1 text-sm text-red-600 ${isRTL ? 'text-right' : 'text-left'}`}>{error}</p>
        )}

        {isOpen &&
          !disabled &&
          dropdownRef.current &&
          createPortal(
            <div
              ref={portalRef}
              className={`fixed z-[99999] bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-hidden ${
                isRTL ? 'text-right' : 'text-left'
              }`}
              style={{
                width: dropdownRef.current.offsetWidth || 'auto',
                top: dropdownRef.current.getBoundingClientRect().bottom + 4 + 'px',
                left: isRTL ? 'auto' : dropdownRef.current.getBoundingClientRect().left + 'px',
                right: isRTL
                  ? window.innerWidth - dropdownRef.current.getBoundingClientRect().right + 'px'
                  : 'auto',
              }}
            >
              <DropdownSearchHeader
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onRefresh={onRefresh}
                isRefreshing={isRefreshing}
                isRTL={isRTL}
                placeholder={t('common.searchPlaceholder')}
              />

              <div ref={listRef} className="overflow-y-auto max-h-48">
                {isLoading ? (
                  <div className={`p-4 text-center text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('common.loading')}
                  </div>
                ) : filteredStores.length === 0 ? (
                  <div className={`p-4 text-center text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {searchTerm ? t('stores.noStoresMatch') : t('stores.noStores')}
                  </div>
                ) : (
                  <>
                    {filteredStores.map((store) => (
                      <div
                        key={store.id}
                        onClick={() => handleSelect(store.id)}
                        className={`px-4 py-2 cursor-pointer hover:bg-primary-50 transition-colors ${
                          normalizeId(store.id) === valueStr ? 'bg-primary-100 font-semibold' : ''
                        } ${isRTL ? 'text-right' : 'text-left'}`}
                      >
                        {formatConfiguredLabel(store, 'store', fieldConfig.listFields, language)}
                      </div>
                    ))}
                    <div
                      ref={sentinelRef}
                      className="h-6 flex items-center justify-center text-xs text-gray-400"
                    >
                      {isFetchingNextPage
                        ? language === 'ar'
                          ? 'جاري تحميل المزيد…'
                          : 'Loading more…'
                        : null}
                    </div>
                  </>
                )}
              </div>
            </div>,
            document.body
          )}
      </div>
    )
  }
)

StoreDropdown.displayName = 'StoreDropdown'

export default StoreDropdown
